export const LEGAL = {
  PENDING: 'pending_legal',
  APPROVED: 'approved_legal',
  REJECTED: 'rejected_legal',
  OVERRIDE: 'override_approved_legal',
  OVERRIDE_REJECTED: 'override_rejected_legal',
} as const;

export const MARKETING = {
  PENDING: 'pending_marketing',
  APPROVED: 'approved_marketing',
  REJECTED: 'rejected_marketing',
  OVERRIDE: 'override_approved_marketing',
  OVERRIDE_REJECTED: 'override_rejected_marketing',
} as const;

export const COMPLETED_LEGAL = new Set([LEGAL.APPROVED, LEGAL.OVERRIDE]);
export const COMPLETED_MARKETING = new Set([MARKETING.APPROVED, MARKETING.OVERRIDE]);

function isTruthyFlag(value: any): boolean {
  return value === true || value === 1 || value === 'true' || value === '1';
}

function normalizeRoleName(name: any): string {
  return String(name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function isSuperAdminRole(role: any): boolean {
  if (!role) return false;
  if (isTruthyFlag(role.is_super_admin) || isTruthyFlag(role.super_admin)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'super admin' || name === 'superadmin' || name === 'administrator';
}

export function isLegalRole(role: any): boolean {
  if (!role) return false;
  if (isSuperAdminRole(role)) return true;
  if (isTruthyFlag(role.is_legal)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'legal' || name.startsWith('legal ');
}

export function isMarketingRole(role: any): boolean {
  if (!role) return false;
  if (isSuperAdminRole(role)) return true;
  if (isTruthyFlag(role.is_marketing)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'marketing' || name.startsWith('marketing ');
}

export function isArRole(role: any): boolean {
  if (!role) return false;
  if (isSuperAdminRole(role)) return true;
  if (isTruthyFlag(role.is_ar_role)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'a&r' || name === 'a & r' || name === 'a and r';
}

/** A&R without treating Super Admin as A&R. */
export function isDedicatedArRole(role: any): boolean {
  if (!role || isSuperAdminRole(role)) return false;
  if (isTruthyFlag(role.is_ar_role)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'a&r' || name === 'a & r' || name === 'a and r';
}

/** Legal actor only — Super Admin and A&R are not dedicated Legal. */
export function isDedicatedLegalRole(role: any): boolean {
  if (!role || isSuperAdminRole(role) || isDedicatedArRole(role)) return false;
  if (isTruthyFlag(role.is_legal)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'legal' || name.startsWith('legal ');
}

/** Marketing actor only — Super Admin and A&R are not dedicated Marketing. */
export function isDedicatedMarketingRole(role: any): boolean {
  if (!role || isSuperAdminRole(role) || isDedicatedArRole(role)) return false;
  if (isTruthyFlag(role.is_marketing)) return true;
  const name = normalizeRoleName(role.name);
  return name === 'marketing' || name.startsWith('marketing ');
}

/** Operations - Approver. View-only on Legal/Marketing Review. */
export function isOperationApproverRole(role: any): boolean {
  if (!role) return false;
  let name = normalizeRoleName(role.name).replace(/[\u2010-\u2015-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (name.endsWith(' role')) name = name.slice(0, -5).trim();
  return name === 'operation approver' || name === 'operations approver';
}

/** Edit Operation by role name, including when that role also has elevated flags. */
export function isEditOperationRole(role: any): boolean {
  if (!role) return false;
  let name = normalizeRoleName(role.name).replace(/-/g, ' ');
  if (name.endsWith(' role')) name = name.slice(0, -5).trim();
  return name === 'edit operation' || name === 'edit operations';
}

/** Record comments on one side: Legal or Marketing for their side, A&R and Edit Operation for either side. */
export function canRecordWorkflowComment(role: any, side: 'legal' | 'marketing'): boolean {
  if (!role) return false;
  if (isDedicatedArRole(role) || isEditOperationRole(role)) return true;
  if (side === 'legal') return isDedicatedLegalRole(role);
  return isDedicatedMarketingRole(role);
}

export function hasLegalAccess(role: any): boolean {
  return isLegalRole(role) || isArRole(role);
}

export function hasMarketingAccess(role: any): boolean {
  return isMarketingRole(role) || isArRole(role);
}

export function hasArOverrideAccess(role: any): boolean {
  return isArRole(role) || isSuperAdminRole(role);
}

export function hasPreReviewAccess(role: any): boolean {
  return hasLegalAccess(role) || hasMarketingAccess(role) || hasArOverrideAccess(role);
}

export function isRejectedLegalStatus(status: string | null | undefined): boolean {
  const value = String(status || '');
  return value === LEGAL.REJECTED || value === LEGAL.OVERRIDE_REJECTED;
}

export function isRejectedMarketingStatus(status: string | null | undefined): boolean {
  const value = String(status || '');
  return value === MARKETING.REJECTED || value === MARKETING.OVERRIDE_REJECTED;
}

export function isRejectedApprovalStatus(
  status: string | null | undefined,
  type: 'legal' | 'marketing',
): boolean {
  return type === 'legal' ? isRejectedLegalStatus(status) : isRejectedMarketingStatus(status);
}

export function legalStatusLabel(status: string | null | undefined): string {
  switch (String(status || '').toLowerCase()) {
    case LEGAL.PENDING: return 'Legal – Pending';
    case LEGAL.APPROVED: return 'Legal – Approved';
    case LEGAL.REJECTED: return 'Legal – Rejected';
    case LEGAL.OVERRIDE: return 'Legal – Override Approved';
    case LEGAL.OVERRIDE_REJECTED: return 'Legal – Override Rejected';
    default: return '';
  }
}

export function marketingStatusLabel(status: string | null | undefined): string {
  switch (String(status || '').toLowerCase()) {
    case MARKETING.PENDING: return 'Marketing – Pending';
    case MARKETING.APPROVED: return 'Marketing – Approved';
    case MARKETING.REJECTED: return 'Marketing – Rejected';
    case MARKETING.OVERRIDE: return 'Marketing – Override Approved';
    case MARKETING.OVERRIDE_REJECTED: return 'Marketing – Override Rejected';
    default: return '';
  }
}

export function productStatusLabel(status: string | null | undefined): string {
  switch (String(status || '').toLowerCase()) {
    case 'pre_review': return 'Pre-Review Approval';
    case 'draft': return 'Draft';
    case 'review': return 'Review';
    case 'approved': return 'Approved';
    case 'hold': return 'On Hold';
    case 'published': return 'Published';
    case 'takedown': return 'Takedown';
    default: return status ? String(status) : '';
  }
}

export function isFilmProduct(product: any): boolean {
  const explicit = String(product?.release_content_type || '').toLowerCase();
  if (explicit === 'film') return true;
  if (explicit === 'non_film' || explicit === 'non-film') return false;
  const genre = typeof product?.genre === 'string' ? product.genre : (product?.genre?.name || '');
  return String(genre).trim() === 'Original Motion Picture Soundtrack';
}
